import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  InputBase,
  Badge,
  useTheme
} from '@mui/material';
import {
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';
import ProfileMenu from './ProfileMenu';
import SmsBalance from './SmsBalance';
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_HEADER_HEIGHT } from './Sidebar';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: '24px',
  backgroundColor: theme.palette.mode === 'dark'
    ? '#1a1a1a'
    : '#f6f6f6',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(0, 0, 0, 0.08)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? '#242424'
      : '#eeeeee',
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.15)'
      : '1px solid rgba(0, 0, 0, 0.12)',
  },
  '&:focus-within': {
    backgroundColor: theme.palette.mode === 'dark'
      ? '#2a2a2a'
      : '#ffffff',
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(0, 0, 0, 0.15)',
  },
  marginRight: theme.spacing(2),
  width: '100%',
  maxWidth: '500px',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.2s ease',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.mode === 'dark' ? '#b3b3b3' : '#6a6a6a',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
  width: '100%',
  height: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    height: '100%',
    fontSize: '0.95rem',
    fontWeight: 400,
    '&::placeholder': {
      color: theme.palette.mode === 'dark' ? '#b3b3b3' : '#6a6a6a',
      opacity: 1,
    },
  },
}));

const Navbar = ({ toggleTheme, isDark, creditsRemaining = 0, isCollapsed = false }) => {
  const theme = useTheme();
  const isElectron = window.electronAPI !== undefined;
  // Pulled from Sidebar.js instead of hardcoded — this is what was causing
  // the navbar/sidebar misalignment: this file had its own 240/80 copy of
  // the widths while Sidebar.js actually renders at 255/76. Importing the
  // same constants keeps the two permanently in sync.
  const sidebarWidth = isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;
  const [zoomPercentage, setZoomPercentage] = useState(100);

  useEffect(() => {
    if (isElectron && window.electronAPI.updateTitleBarTheme) {
      window.electronAPI.updateTitleBarTheme(isDark);
    }
  }, [isDark, isElectron]);

  useEffect(() => {
    if (!isElectron) return;

    // Get initial zoom level
    window.electronAPI.getZoomLevel().then(({ percentage }) => {
      setZoomPercentage(percentage);
    });

    // Listen for zoom changes
    const cleanup = window.electronAPI.onZoomChanged(({ percentage }) => {
      setZoomPercentage(percentage);
    });

    return cleanup;
  }, [isElectron]);

  // Calculate dynamic margin based on zoom percentage
  const calculateRightMargin = () => {
    if (!isElectron) return 0;
    const baseMargin = 120;
    const zoomFactor = 100 / zoomPercentage;
    return Math.round(baseMargin * zoomFactor);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme => theme.zIndex.drawer + 1,
        height: `${SIDEBAR_HEADER_HEIGHT}px`,
        width: `calc(100% - ${sidebarWidth}px)`,
        marginLeft: `${sidebarWidth}px`,
        transition: theme => theme.transitions.create(['width', 'margin-left'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        backgroundImage: theme.palette.mode === 'dark'
          ? 'linear-gradient(to bottom, #2b323c 95%, rgba(0, 0, 0, 0.3))'
          : 'linear-gradient(to bottom, white 95%, rgba(0, 0, 0, 0.05))',
        boxShadow: 'none',
        borderBottom: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(0, 0, 0, 0.08)',
        WebkitAppRegion: isElectron ? 'drag' : 'initial',
      }}
    >
      <Toolbar
        sx={{
          height: '100%',
          paddingLeft: 3,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ WebkitAppRegion: 'no-drag', flex: '0 1 auto' }}>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search..."
              inputProps={{ 'aria-label': 'search' }}
            />
          </Search>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            WebkitAppRegion: 'no-drag',
            flex: '0 0 auto',
            marginRight: `${calculateRightMargin()}px`,
            transition: 'margin-right 0.2s ease',
          }}
        >
          <SmsBalance />

          <IconButton
            onClick={toggleTheme}
            sx={{
              color: '#1976d2',
              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' }
            }}
          >
            {isDark ? <LightIcon /> : <DarkIcon />}
          </IconButton>

          <IconButton
            sx={{
              color: '#1976d2',
              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' }
            }}
          >
            <Badge badgeContent={4} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <IconButton
            sx={{
              color: '#1976d2',
              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' }
            }}
          >
            <Badge badgeContent={creditsRemaining} color="primary">
              <WalletIcon />
            </Badge>
          </IconButton>

          <ProfileMenu />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;